import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DeviceProperty} from "../../../../../model/device-property";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {PropertyType} from "../../../../../model/property-type";
import {ActionButton} from "../../../../../model/ui/action-button";
import {PreviousRouteService} from "../../../../../services/previous-route.service";
import {noSpecialCharacters} from "../../../../../shared/custom-validators";
import {translate} from "@ngneat/transloco";
import {DefaultPropertyTypesService} from "../../../../../services/default-property-types.service";
import {HwCatalogService} from "../../../../../services/hw-catalog.service";
import {DefaultDeviceCatalog} from "../../../../../model/default-device-catalog";

@Component({
  selector: 'app-default-devices-form-properties',
  templateUrl: './default-devices-form-properties.component.html',
  styleUrls: ['./default-devices-form-properties.component.scss']
})
export class DefaultDevicesFormPropertiesComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() type: string;
  @Input() deviceProperties: DeviceProperty[];
  @Output() devicePropertiesChange = new EventEmitter<DeviceProperty[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  defaultDeviceProperties: DeviceProperty[];
  defaultDevicePropertiesForm: FormGroup;
  rowsForm: FormArray;

  defaultPropertyTypes: PropertyType[];

  displayCopyFromDefaultDevice: boolean;
  copyFromDefaultDeviceActions: ActionButton[];

  constructor(private defaultPropertyTypesService: DefaultPropertyTypesService,
              private hwCatalogService: HwCatalogService,
              private formBuilder: FormBuilder,
              private previousService: PreviousRouteService) {
  }

  ngOnInit(): void {
    this.defaultDeviceProperties = this.deviceProperties;
    this.buildCopyFromDefaultDeviceActions();
    this.loadAndCreateForm();
  }

  private loadAndCreateForm(): void {
    this.defaultPropertyTypesService.getDefaultPropertyTypes().toPromise().then(response => {
      this.defaultPropertyTypes = response.defaultPropertyTypes
        .sort((a, b) => a.name > b.name ? 1 : -1);
      this.createForm(this.defaultDeviceProperties);
    });
  }

  private createForm(defaultDeviceProperties: DeviceProperty[]): void {
    this.rowsForm = this.formBuilder.array(
      defaultDeviceProperties.map(property => this.formGroupFromProperty(property))
    );
    this.defaultDevicePropertiesForm = this.formBuilder.group({
      defaultDeviceProperties: this.rowsForm
    });
  }

  addProperty(): void {
    const newProperty = this.formGroupFromProperty(new DeviceProperty());
    this.rowsForm.push(newProperty);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
  }

  deleteAllRows(): void {
    while (this.rowsForm.length > 0) {
      this.rowsForm.removeAt(0);
    }
  }

  onPreviousStep(): void {
    const properties = this.defaultDevicePropertiesForm.get('defaultDeviceProperties').value;
    this.previousStep.emit();
    this.devicePropertiesChange.emit(properties);
  }

  onNextStep(): void {
    if (this.defaultDevicePropertiesForm.valid) {
      const properties = this.defaultDevicePropertiesForm.get('defaultDeviceProperties').value;
      this.nextStep.emit();
      this.devicePropertiesChange.emit(properties);
    }
  }

  private formGroupFromProperty(property: DeviceProperty): FormGroup {
    return this.formBuilder.group({
      name: [property.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      value: [property.value],
      propertyType: [property.propertyType, [Validators.required]],
      uuid: [property.uuid]
    });
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

  private buildCopyFromDefaultDeviceActions(): void {
    this.copyFromDefaultDeviceActions = [
      {
        label: translate('misc.copyProperties'),
        icon: 'pi pi-copy',
        action: (defaultDeviceCatalog: DefaultDeviceCatalog) => this.copyProperties(defaultDeviceCatalog)
      }, {
        label: translate('misc.appendProperties'),
        icon: 'pi pi-plus',
        action: (defaultDeviceCatalog: DefaultDeviceCatalog) => this.copyProperties(defaultDeviceCatalog, true)
      }
    ];
  }

  copyProperties(defaultDeviceCatalog: DefaultDeviceCatalog, append: boolean = false): void {
    const measurements = this.defaultDevicePropertiesForm.get('defaultDeviceProperties').value;
    this.hwCatalogService.getDefaultDeviceCatalog(defaultDeviceCatalog.uuid).toPromise().then(response => {
      if (!append) {
        this.deleteAllRows();
      }
      let fullDeviceCatalog = response.defaultDeviceCatalog;
      if (fullDeviceCatalog.content?.deviceProperties) {
        fullDeviceCatalog.content.deviceProperties.forEach((property: DeviceProperty) => {
          property.uuid = null;
          if (measurements.find(sensor => sensor.name === property.name) === undefined) {
            this.rowsForm.push(this.formGroupFromProperty(property));
          }
        });
      }
      this.displayCopyFromDefaultDevice = false;
    });
  }

}
